<template>
  <b-modal 
    id="modal-add-batch" 
    centered 
    hide-footer
    :title="isEditMode ? 'Edit Batch' : 'Add Batch'"
    size="xl"
    body-class="p-0"
    :no-close-on-backdrop="isSubmitting"
    :no-close-on-esc="isSubmitting"
    :hide-header-close="isSubmitting">
    <div class="modal-container">
      <!-- Body -->
      <div class="modal-body p-4">
        <div
          v-if="!isEditMode && isMoodlePlatform"
          class="alert alert-warning mb-3"
          style="font-size: 0.875rem;"
        >
          <i class="fe fe-info me-2"></i>
          <span v-if="!hasMappedMoodleCourse">
            Batch pertama akan membuat course Moodle kosong. Isi materi di Moodle pada batch ini dulu.
            Batch berikutnya akan membawa isi course yang sama.
          </span>
          <span v-else>
            Course Moodle akan di-clone dari batch yang sudah ter-mapping (materi, activity, quiz, filter).
            Peserta tidak ikut ter-copy — enroll ulang lewat alur enroll yang ada. Proses clone bisa sampai sekitar 5 menit.
          </span>
        </div>

        <!-- Batch Section -->
        <h4 class="font-weight-bold mb-3 p-3 rounded w-100" style="background-color: #FAFAFA;">Batch</h4>
        
        <div class="row">
          <!-- Course Method -->
          <div class="col-md-6 mb-3">
            <label class="form-label">
              Course Method<span class="text-danger">*</span>
            </label>
            <v-select
              v-model="formData.courseMethod"
              :options="trainingMethodOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Select Course Method"
              :loading="isLoadingMethods"
              :class="{ 'is-invalid': !formData.courseMethod && validationTriggered }"
              >
            </v-select>
            <div v-if="!formData.courseMethod && validationTriggered" class="invalid-feedback d-block">
              Course Method is required
            </div>
          </div>

          <!-- Estimate Course Price -->
          <div class="col-md-6 mb-3">
            <label class="form-label">
              Estimate Course Price
            </label>
            <currency-input
              v-model="formData.coursePrice"
              :currency.sync="formData.coursePriceCurrency"
              placeholder="2000000"
              :currency-options="currencyOptions">
            </currency-input>
          </div>

          <!-- Organizer -->
          <div v-if="isMoodlePlatform" class="col-md-6 mb-3">
            <label class="form-label">
              Penyelenggara<span class="text-danger">*</span>
            </label>
            <v-select
              v-model="formData.penyelenggara"
              :options="penyelenggaraOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Select Penyelenggara"
              :class="{ 'is-invalid': !formData.penyelenggara && validationTriggered }">
            </v-select>
            <div v-if="!formData.penyelenggara && validationTriggered" class="invalid-feedback d-block">
              Penyelenggara is required
            </div>
          </div>

          <!-- Estimated Accommodation Price -->
          <div class="col-md-6 mb-3">
            <label class="form-label">
              Estimated Acomodation Price (Hotel)
            </label>
            <currency-input
              v-model="formData.accommodationPrice"
              :currency.sync="formData.accommodationCurrency"
              placeholder="1000000"
              :currency-options="currencyOptions">
            </currency-input>
          </div>

          <!-- Estimated Transport Price -->
          <div class="col-md-6 mb-3">
            <label class="form-label">
              Estimated Transport Price (Ticket & Others)
            </label>
            <currency-input
              v-model="formData.transportPrice"
              :currency.sync="formData.transportCurrency"
              placeholder="700000"
              :currency-options="currencyOptions">
            </currency-input>
          </div>

          <!-- Location/Training room -->
          <div class="col-md-6 mb-3">
            <label class="form-label">
              Location/Training room<span class="text-danger">*</span>
            </label>
            <input 
              type="text" 
              class="form-control" 
              :class="{ 'is-invalid': !formData.location && validationTriggered }"
              placeholder="Jakarta"
              v-model="formData.location">
            <div v-if="!formData.location && validationTriggered" class="invalid-feedback">
              Lokasi wajib diisi
            </div>
          </div>

          <!-- Quota Participant -->
          <div class="col-md-6 mb-3">
            <label class="form-label">
              Quota Participant<span class="text-danger">*</span>
            </label>
            <input 
              type="number" 
              class="form-control" 
              :class="{ 'is-invalid': !formData.quota && validationTriggered }"
              placeholder="20"
              v-model="formData.quota">
            <div v-if="!formData.quota && validationTriggered" class="invalid-feedback">
              Quota Participant is required
            </div>
          </div>

          <!-- Passing Score - Only for Ugems University -->
          <div v-if="platform === 'Ugems University'" class="col-md-6 mb-3">
            <label class="form-label">
              Passing Score<span v-if="competencyType === 'Internal' || competencyType === 'Thematic'" class="text-danger">*</span>
            </label>
            <input 
              type="number" 
              class="form-control" 
              :class="{ 'is-invalid': !formData.passing_score && validationTriggered && (competencyType === 'Internal' || competencyType === 'Thematic') }"
              placeholder="75"
              min="0"
              max="100"
              v-model="formData.passing_score">
            <div v-if="!formData.passing_score && validationTriggered && (competencyType === 'Internal' || competencyType === 'Thematic')" class="invalid-feedback">
              Passing Score is required
            </div>
          </div>
        </div>

        <!-- Certificate Section -->
        <h4 class="font-weight-bold mb-3 mt-4 p-3 rounded w-100" style="background-color: #FAFAFA;">Certificate</h4>
        
        <!-- Certificate Preview -->
        <div class="col-md-6 mb-3">
          <label class="form-label">Certificate Preview</label>
          <div 
            class="certificate-preview border rounded p-3 text-center cursor-pointer"
            @click="openCertificateTemplate">
            <img 
              v-if="selectedCertificate"
              :src="selectedCertificate.image" 
              alt="Certificate"
              class="img-fluid"
              style="max-height: 200px;">
            <div v-else class="text-muted py-5">
              <i class="fe fe-image mb-2" style="font-size: 3rem;"></i>
              <p>Click to choose certificate template</p>
              <button 
                type="button"
                class="btn btn-sm btn-danger">
                Choose
              </button>
            </div>
          </div>
        </div>

        <!-- Schedule Section -->
        <div class="d-flex justify-content-between align-items-center mb-3 mt-4 rounded" style="background-color: #FAFAFA;">
          <h4 class="font-weight-bold mb-0 p-3">Schedule<span class="text-danger">*</span></h4>
          <button class="btn btn-danger btn-sm" @click="addSchedule">
            <i class="fe fe-plus me-1"></i>
            Add Schedule
          </button>
        </div>
        <!-- Schedule List -->
        <div v-for="(schedule, index) in formData.schedules" :key="index" class="row mb-3">
          <div class="col-md-5">
            <label class="form-label">Date<span class="text-danger">*</span></label>
            <input 
              type="date" 
              class="form-control"
              :class="{ 'is-invalid': !schedule.date && scheduleValidationTriggered }"
              :min="todayDate"
              v-model="schedule.date">
          </div>
          <div class="col-md-3">
            <label class="form-label">Start Time<span class="text-danger">*</span></label>
            <input 
              type="time" 
              class="form-control"
              :class="{ 'is-invalid': !schedule.startTime && scheduleValidationTriggered }"
              v-model="schedule.startTime">
          </div>
          <div class="col-md-3">
            <label class="form-label">End Time<span class="text-danger">*</span></label>
            <input 
              type="time" 
              class="form-control"
              :class="{ 'is-invalid': !schedule.endTime && scheduleValidationTriggered }"
              v-model="schedule.endTime">
          </div>
          <div class="col-md-1 d-flex align-items-end">
            <button 
              class="btn btn-danger btn-sm w-100"
              @click="removeSchedule(index)">
              <i class="fe fe-trash-2"></i>
            </button>
          </div>
        </div>


      </div>

      <!-- Footer -->
      <div class="modal-footer border-top">
        <button 
          class="btn btn-danger px-4" 
          @click="submitBatch"
          :disabled="isSubmitting">
          <span v-if="isSubmitting">
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {{ submittingLabel }}
          </span>
          <span v-else>Submit</span>
        </button>
      </div>
    </div>

    <!-- Certificate Template Modal -->
    <modal-certificate-template 
      @certificate-selected="onCertificateSelected" />
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ModalCertificateTemplate from './ModalCertificateTemplate.vue';
import CurrencyInput from '@/components/CurrencyInput.vue';

const DEFAULT_PENYELENGGARA = 'PT BORNEO INDOBARA';
const PENYELENGGARA_OPTIONS = [
  { label: 'Borneo Indobara', value: 'PT BORNEO INDOBARA' },
  { label: 'Cipta Kridatama', value: 'PT CIPTA KRIDATAMA' },
  { label: 'Tama Mulia Resources', value: 'PT TAMA MULIA RESOURCES' },
  { label: 'Putra Perkasa Abadi', value: 'PT PUTRA PERKASA ABADI' }
];

export default {
  name: 'ModalAddBatch',
  components: {
    ModalCertificateTemplate,
    CurrencyInput
  },
  props: {
    platform: {
      type: String,
      default: ''
    },
    competencyType: {
      type: String,
      default: ''
    },
    courseId: {
      type: [Number, String],
      default: null
    },
    waitForApiResponse: {
      type: Boolean,
      default: false // false = immediate close (ModalAddCourse), true = wait for API (ModalCourseDetail)
    },
    hasMappedMoodleCourse: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters('modalAddBatch', {
      trainingMethodOptions: 'getTrainingMethodOptions',
      isLoadingMethods: 'getLoadingMethods'
    }),
    submittingLabel() {
      if (this.isEditMode || !this.isMoodlePlatform) {
        return 'Mengirim...';
      }
      return this.hasMappedMoodleCourse
        ? 'Clone Moodle sedang berjalan...'
        : 'Membuat course Moodle...';
    },
    todayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    isMoodlePlatform() {
      const normalizedPlatform = (this.platform || '').toLowerCase();
      return ['bib academy', 'ugems university', 'ugems univ', 'gems univ', 'my learning'].includes(normalizedPlatform);
    }
  },
  data() {
    return {
      isEditMode: false, // Track if modal is in edit mode
      isSubmitting: false, // Track if form is being submitted
      currencyOptions: ['IDR', 'USD'],
      searchTimeout: null,
      validationTriggered: false,
      scheduleValidationTriggered: false,
      penyelenggaraOptions: PENYELENGGARA_OPTIONS,

      selectedCertificate: null,
      
      formData: {
        courseMethod: '',
        penyelenggara: DEFAULT_PENYELENGGARA,
        accommodationCurrency: 'IDR',
        accommodationPrice: '',
        transportCurrency: 'IDR',
        transportPrice: '',
        coursePriceCurrency: 'IDR',
        coursePrice: '',
        location: '',
        quota: '',
        passing_score: '',
        certificate_id: null, // Only store certificate_id
        schedules: [
          { id: undefined, date: '', startTime: '', endTime: '', status: 'upcoming' }
        ]
      }
    }
  },

  methods: {
    ...mapActions('modalAddBatch', ['getTrainingMethods']),
    ...mapActions('certificate', ['getCertificateById']),

    closeModal() {
      this.$bvModal.hide('modal-add-batch');
    },

    openCertificateTemplate() {
      this.$bvModal.show('modal-certificate-template');
    },

    onCertificateSelected(certificate) {
      // Only store certificate_id and display info
      this.selectedCertificate = {
        id: certificate.certificate_id,
        name: certificate.certificate_name,
        image: certificate.certificate_image
      };
      this.formData.certificate_id = certificate.certificate_id;
    },

    addSchedule() {
      this.formData.schedules.push({
        id: undefined,
        date: '',
        startTime: '',
        endTime: '',
        status: 'upcoming'
      });
    },

    removeSchedule(index) {
      if (this.formData.schedules.length > 1) {
        this.formData.schedules.splice(index, 1);
      } else {
        this.$swal('Warning', 'Minimal satu jadwal wajib diisi', 'warning');
      }
    },

    validateSchedules() {
      // Check if at least one schedule exists
      if (this.formData.schedules.length === 0) {
        return { valid: false, message: 'Minimal satu jadwal wajib diisi' };
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison

      // Check if all schedules are filled
      for (let i = 0; i < this.formData.schedules.length; i++) {
        const schedule = this.formData.schedules[i];
        if (!schedule.date || !schedule.startTime || !schedule.endTime) {
          return { valid: false, message: `Jadwal ${i + 1} belum lengkap. Isi tanggal, waktu mulai, dan waktu selesai.` };
        }

        // Check if schedule date is not in the past
        const scheduleDate = new Date(schedule.date);
        scheduleDate.setHours(0, 0, 0, 0);
        if (scheduleDate < today) {
          return { valid: false, message: `Tanggal jadwal ${i + 1} tidak boleh di masa lalu. Pilih hari ini atau tanggal mendatang.` };
        }
      }

      return { valid: true, message: '' };
    },

    submitBatch() {
      // Trigger validation styling
      this.validationTriggered = true;
      this.scheduleValidationTriggered = true;

      // Validate mandatory fields
      if (!this.formData.courseMethod) {
        this.$swal('Warning', 'Pilih metode pelatihan', 'warning');
        return;
      }

      if (this.isMoodlePlatform && !this.formData.penyelenggara) {
        this.$swal('Warning', 'Please select Penyelenggara', 'warning');
        return;
      }

      if (!this.formData.location) {
        this.$swal('Warning', 'Isi lokasi/ruang pelatihan', 'warning');
        return;
      }

      if (!this.formData.quota) {
        this.$swal('Warning', 'Isi kuota peserta', 'warning');
        return;
      }

      // Validate passing_score for Internal/Thematic competency type
      if (this.platform === 'Ugems University' && (this.competencyType === 'Internal' || this.competencyType === 'Thematic')) {
        if (!this.formData.passing_score || this.formData.passing_score === '') {
          this.$swal('Warning', 'Isi passing score untuk pelatihan Internal/Tematik', 'warning');
          return;
        }
        const passingScore = parseInt(this.formData.passing_score);
        if (isNaN(passingScore) || passingScore < 0 || passingScore > 100) {
          this.$swal('Warning', 'Passing score harus antara 0 dan 100', 'warning');
          return;
        }
      }

      // Validate schedules
      const scheduleValidation = this.validateSchedules();
      if (!scheduleValidation.valid) {
        this.$swal('Warning', scheduleValidation.message, 'warning');
        return;
      }

      // Prepare data for API - convert formatted prices to plain numbers
      const dataToSend = {
        ...this.formData,
        accommodationPrice: parseInt(this.formData.accommodationPrice) || 0,
        transportPrice: parseInt(this.formData.transportPrice) || 0,
        coursePrice: parseInt(this.formData.coursePrice) || 0,
        penyelenggara: this.formData.penyelenggara || DEFAULT_PENYELENGGARA,
        certificate_id: this.formData.certificate_id, // Only send certificate_id
        schedules: this.formData.schedules.map(schedule => ({
          id: schedule.id, // Will be defined for existing schedules, undefined for new ones
          date: schedule.date,
          startTime: schedule.startTime,
          endTime: schedule.endTime,
          status: schedule.status
        }))
      };

      // Add passing_score only for Internal/Thematic competency type
      if (this.competencyType === 'Internal' || this.competencyType === 'Thematic') {
        dataToSend.passing_score = parseInt(this.formData.passing_score) || 0;
      } else {
        // Remove passing_score if competency type is not Internal/Thematic
        delete dataToSend.passing_score;
      }
      
      // Check if we need to wait for API response
      if (this.waitForApiResponse) {
        // Set submitting state for API calls
        this.isSubmitting = true;
        
        // Emit event and wait for parent to call handleSuccess or handleError
        if (this.isEditMode) {
          this.$emit('batch-updated', dataToSend);
        } else {
          this.$emit('batch-added', dataToSend);
        }
      } else {
        // For course creation flow - emit and close immediately (no API call)
        if (this.isEditMode) {
          this.$emit('batch-updated', dataToSend);
        } else {
          this.$emit('batch-added', dataToSend);
        }
        
        // Close modal immediately as no API call is needed
        this.resetForm();
        this.closeModal();
      }
    },

    async loadBatchData(batchData) {
      // Load batch data for editing
      this.isEditMode = true;
      
      // Load form data
      this.formData = {
        id: batchData.id || undefined,
        courseMethod: batchData.courseMethod,
        penyelenggara: batchData.penyelenggara || DEFAULT_PENYELENGGARA,
        accommodationCurrency: batchData.accommodationCurrency || 'IDR',
        accommodationPrice: batchData.accommodationPrice?.toString() || '',
        transportCurrency: batchData.transportCurrency || 'IDR',
        transportPrice: batchData.transportPrice?.toString() || '',
        coursePriceCurrency: batchData.coursePriceCurrency || 'IDR',
        coursePrice: batchData.coursePrice?.toString() || '',
        location: batchData.location,
        quota: batchData.quota,
        passing_score: batchData.passing_score?.toString() || '',
        certificate_id: batchData.certificate_id,
        // Preserve schedule IDs from course detail
        schedules: batchData.schedules.map(schedule => ({
          id: schedule.id,
          date: schedule.date,
          startTime: schedule.startTime,
          endTime: schedule.endTime,
          status: schedule.status || 'upcoming'
        }))
      };
      
      // Load certificate preview if exists
      if (batchData.certificate_id) {
        try {
          const response = await this.getCertificateById(batchData.certificate_id);
          
          if (response && response.data) {
            const certificateData = response.data;
            
            this.selectedCertificate = {
              id: certificateData.id,
              name: certificateData.certificate_name || 'Certificate',
              image: certificateData.image_url 
                ? `${process.env.VUE_APP_API_URL_UGEMS_UNIV}/attachments/${certificateData.image_url}`
                : null
            };
            
            console.log('✅ Certificate loaded:', this.selectedCertificate);
          }
        } catch (error) {
          console.error('Error loading certificate:', error);
          this.$swal('Error', 'Detail sertifikat belum berhasil dimuat', 'error');
        }
      }
    },

    handleSuccess() {
      // Called by parent component after successful API call
      this.isSubmitting = false;
      this.resetForm();
      this.closeModal();
    },

    handleError() {
      // Called by parent component if API call fails
      this.isSubmitting = false;
      // Keep modal open so user can retry
    },

    resetForm() {
      // Reset form to initial state
      this.isEditMode = false;
      this.isSubmitting = false;
      this.selectedCertificate = null;
      this.validationTriggered = false;
      this.scheduleValidationTriggered = false;
      
      this.formData = {
        courseMethod: '',
        penyelenggara: DEFAULT_PENYELENGGARA,
        accommodationCurrency: 'IDR',
        accommodationPrice: '',
        transportCurrency: 'IDR',
        transportPrice: '',
        coursePriceCurrency: 'IDR',
        coursePrice: '',
        location: '',
        quota: '',
        passing_score: '',
        certificate_id: null,
        schedules: [
          { id: undefined, date: '', startTime: '', endTime: '', status: 'upcoming' }
        ]
      };
    }
  },

  mounted() {
    this.$root.$on('bv::modal::shown', (bvEvent, modalId) => {
      if (modalId === 'modal-add-batch') {
        // Load training methods
        this.getTrainingMethods();
      }
    });
    
    
    // Listen for modal hidden event to reset form
    this.$root.$on('bv::modal::hidden', (bvEvent, modalId) => {
      if (modalId === 'modal-add-batch') {
        this.resetForm();
      }
    });
  },

  beforeDestroy() {
    this.$root.$off('bv::modal::hidden');
  }
}
</script>

<style scoped>
.modal-container {
  min-height: 400px;
}

.modal-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-close {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.btn-close:hover {
  opacity: 1;
}

.certificate-preview {
  background-color: #f8f9fa;
  min-height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.certificate-preview:hover {
  background-color: #e9ecef;
  border-color: #e63757 !important;
}

.cursor-pointer {
  cursor: pointer;
}

/* V-Select Custom Styles */
/* ::v-deep .v-select .vs__dropdown-toggle {
  border-color: #ced4da;
  border-radius: 0.375rem;
  padding: 0.375rem 0.75rem;
}

::v-deep .v-select.vs--open .vs__dropdown-toggle {
  border-color: #e63757;
  box-shadow: 0 0 0 0.2rem rgba(230, 55, 87, 0.25);
}

::v-deep .v-select .vs__selected {
  color: #2d3748;
  margin: 0;
  padding: 0;
}

::v-deep .v-select .vs__search {
  margin: 0;
  padding: 0;
}

::v-deep .v-select .vs__dropdown-menu {
  border-color: #e63757;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

::v-deep .v-select .vs__dropdown-option--highlight {
  background-color: #e63757;
  color: white;
} */
</style>
